"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, FileText, Radio, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const features = [{
  icon: Radio,
  title: "Wireless Connectivity",
  description: "Continuous wireless and battery-powered data transmission during active rotation and blending."
}, {
  icon: ShieldCheck,
  title: "ATEX & Class I Div 1",
  description: "Certified for hazardous manufacturing areas and harsh industrial environments."
}, {
  icon: Cpu,
  title: "Linear Variable Filter",
  description: "Solid-state optical engine with no moving parts for repeatable spectral measurement."
}, {
  icon: Activity,
  title: "Real-Time End-Point",
  description: "Direct process control integration for automated drying and blend uniformity detection."
}];
const specifications = [{
  label: "Wavelength Range",
  value: "950 – 1650 nm"
}, {
  label: "Enclosure Rating",
  value: "IP67 Washdown"
}, {
  label: "Operating Mode",
  value: "Wireless / USB"
}, {
  label: "Mounting",
  value: "Sanitary Flange & Port"
}];
export default function MicroNir() {
  const shouldReduceMotion = useReducedMotion();
  const initialMotion = shouldReduceMotion ? {
    opacity: 1
  } : {
    opacity: 0,
    y: 16
  };
  const animateMotion = {
    opacity: 1,
    y: 0
  };
  const transitionConfig = {
    duration: 0.45,
    ease: "easeOut"
  };
  return <section id="heromicronirpatw" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Content */}
          <motion.div initial={initialMotion} animate={animateMotion} transition={transitionConfig} className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-medium uppercase tracking-wider">
                Process Analytical Technology
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-xs text-muted-foreground">
                Near-Infrared Spectrometry
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
                MicroNIR PAT-W Process Spectrometer
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                A compact, wireless near-infrared spectrometer designed for real-time monitoring of blending, drying, and formulation processes in regulated manufacturing facilities across Ireland and the UK.
              </p>
            </div>

            {/* Quick Specs Strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 border-y border-border">
              {specifications.map((spec, index) => <div key={spec.label} data-index={index} className="space-y-1">
                  <span className="text-xs text-muted-foreground block">{spec.label}</span>
                  <span className="text-sm font-semibold text-foreground block">{spec.value}</span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Your Application
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/BookADemoPage">Book a Technical Demo</Link>
              </Button>
              <Button asChild variant="ghost" size="lg">
                <Link to="/NirProcessSpectrometersPage">
                  <FileText className="size-4" />
                  View All NIR Spectrometers
                </Link>
              </Button>
            </div>

            {/* Compliance bullet points */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                21 CFR Part 11 compliant software
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                USP &lt;1119&gt; and EP 2.2.40 verification
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                Full qualification &amp; lifecycle support
              </span>
            </div>
          </motion.div>

          {/* Right Column: Visual & Core Features */}
          <motion.div initial={initialMotion} animate={animateMotion} transition={{
          ...transitionConfig,
          delay: 0.1
        }} className="lg:col-span-5 flex flex-col gap-6">
            {/* Visual Container */}
            <div className="relative rounded-lg border border-border bg-card p-6 shadow-sm overflow-hidden">
              <div className="relative z-10 space-y-4">
                <div className="aspect-video w-full rounded-md bg-muted overflow-hidden relative border border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_w_spectrometer_instrument.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_w_spectrometer_instrument.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_w_spectrometer_instrument.png-wvc-sizes" alt="MicroNIR PAT-W spectrometer mounted on process equipment" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                  <div className="absolute top-3 right-3">
                    <Badge variant="default" className="bg-primary text-primary-foreground text-xs">
                      Wireless PAT
                    </Badge>
                  </div>
                </div>

                {/* Feature Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  {features.map((feature, idx) => {
                  const IconComponent = feature.icon;
                  return <div key={feature.title} data-index={idx} className="p-3 rounded-md border border-border bg-background flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-sm bg-secondary text-secondary-foreground">
                            <IconComponent className="size-4" />
                          </div>
                          <h2 className="text-xs font-semibold text-foreground">
                            {feature.title}
                          </h2>
                        </div>
                        <p className="text-xs text-muted-foreground leading-snug">
                          {feature.description}
                        </p>
                      </div>;
                })}
                </div>
              </div>
            </div>

            {/* Quick Link Card */}
            <div className="p-4 rounded-lg bg-secondary text-secondary-foreground border border-border flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Support &amp; Integration
                </p>
                <p className="text-sm font-medium text-foreground">
                  Need on-site qualification or feasibility data?
                </p>
              </div>
              <Button asChild size="sm" variant="outline">
                <Link to="/FeasibilityStudiesPage">Explore Studies</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}